import { useCallback, useEffect, useRef, useState } from 'react';

import { TABLE_BRAKE_LINING } from '@/features/common/components/dialogs/dialogConstants';
import { CommonSortBy, useCommonQueryParams } from '@/features/common/hooks/useCommonQueryParams';
import { useUserSettings } from '@/features/common/hooks/useUserSettings';
import { BrakeLiningSettings, Status } from '@/generated/GeneratedApiTypes';
import { GetBrakesRequestParams } from '@/services/vehicleDiagnosticsApi';

const BRAKE_ALLOWED_SORT_BY = ['axle', 'side', 'value'] as const;
type BrakeSorting = (typeof BRAKE_ALLOWED_SORT_BY)[number];
export type BrakeSortBy = CommonSortBy | BrakeSorting;

export interface RangeValue {
    from?: number;
    to?: number;
}

export const useBrakesQueryParams = ({
    pageNumber,
    pageSize,
    assetIds,
    workshopIds,
    accountIds,
}: Partial<GetBrakesRequestParams>) => {
    const { settings, saveSettings, isSettingsError } = useUserSettings<BrakeLiningSettings>(TABLE_BRAKE_LINING);
    const settingsReady = useRef(false);

    const [minState, setMinState] = useState<number>(0);
    const [maxState, setMaxState] = useState<number>(100);
    const [criticalityStatus, setCriticalityStatus] = useState<Status[]>([]);
    const [sortBy, setSortByState] = useState<BrakeSortBy>('vehicle');
    const [sortDirection, setSortDirectionState] = useState<'ASC' | 'DESC'>('ASC');

    useEffect(() => {
        if (settingsReady.current) return;
        if (!settings && !isSettingsError) return;
        setMinState(settings?.filters?.min ?? 0);
        setMaxState(settings?.filters?.max ?? 100);
        setCriticalityStatus((settings?.filters?.status as unknown as Status[]) ?? []);
        setSortByState((settings?.sort?.sort_by as BrakeSortBy) ?? 'vehicle');
        setSortDirectionState(settings?.sort?.sort_direction ?? 'ASC');
        settingsReady.current = true;
    }, [settings, isSettingsError]);

    const commonQueryParams = useCommonQueryParams<BrakeSortBy>({
        sortBy,
        sortDirection,
        pageNumber,
        pageSize,
        assetIds,
        workshopIds,
        accountIds,
    });

    const setRange = useCallback(({ from, to }: RangeValue) => {
        setMinState(from ?? 0);
        setMaxState(to ?? 100);
    }, []);

    useEffect(() => {
        if (!settingsReady.current) {
            return;
        }
        saveSettings({
            filters: { min: minState, max: maxState, status: criticalityStatus },
            sort: {
                sort_by: commonQueryParams.sorting.sortBy ?? undefined,
                sort_direction: commonQueryParams.sorting.sortDirection ?? undefined,
            },
        });
    }, [
        minState,
        maxState,
        criticalityStatus,
        commonQueryParams.sorting.sortBy,
        commonQueryParams.sorting.sortDirection,
    ]);

    return {
        min: minState,
        setMin: setMinState,
        max: maxState,
        setMax: setMaxState,
        setRange,
        criticalityStatus,
        setCriticalityStatus,
        settingsReady: settingsReady.current,
        ...commonQueryParams,
    };
};
